export function sequence<T>(elements: T[], sameGroup: (a: T, b: T) => boolean, groupSize?: number): T[][] {
  const groups: T[][] = [];
  let lastGroup: T[] = [];
  let lastElement: T | undefined;

  elements.forEach((element) => {
    const exceded = groupSize !== undefined ? lastGroup.length >= groupSize : false;

    if (exceded || (lastElement && !sameGroup(lastElement, element))) {
      groups.push(lastGroup);
      lastGroup = [];
    }
    lastGroup.push(element);
    lastElement = element;
  });

  groups.push(lastGroup);

  return groups;
}

export function paginate<T>(content: T[], page: number, perPage: number): T[] {
  const offset = page * perPage;
  return content.slice(offset, offset + perPage);
}

export function idOf(object: string | { id: string; slug?: string | null }, useId = false): string {
  return typeof object === 'string' ? object : useId ? object.id : object.slug || object.id;
}

export function indexBy<T extends object>(collection: T[], key: keyof T): { [key: string]: T } {
  const obj: { [key: string]: T } = {};
  collection.forEach((e) => (obj[e[key] as unknown as string] = e));
  return obj;
}

export function transpose<T>(content: T[][]): T[][] {
  return content[0].map((_, i) => content.map((x) => x[i]));
}

export function indexArrayByKey<T>(array: T[], key: keyof T): Record<string, T> {
  const result: Record<string, T> = {};
  array.forEach((el) => {
    // technically key of el can be a symbol as well, but since we aren't using that we can safely ignore it.
    const indexingKey = el[key] as unknown as string;
    result[indexingKey] = el;
  });
  return result;
}

export function getSequences(values: string[]): string[] {
  const allNumericValues = values.every((value) => !isNaN(parseInt(value)));

  if (!allNumericValues) {
    return values.sort();
  }

  const numericValues = values
    .map((value) => {
      return parseInt(value);
    })
    .sort((a, b) => a - b);

  const result: Array<[number, number]> = [[numericValues[0], numericValues[0]]];
  numericValues.shift();
  numericValues.forEach((el) => {
    // array has atleast 1 element
    const currentRange = result.pop() as [number, number];
    if (el === currentRange[1] + 1) {
      currentRange[1] = el;
      result.push(currentRange);
    } else {
      result.push(currentRange);
      result.push([el, el]);
    }
  });

  return result.map(([low, high]) => (low === high ? `${low}` : `${low}-${high}`));
}

export function sumByKey<T, K extends keyof T>(array: T[], field: K): number {
  return array.reduce((accumulator, currentValue) => {
    if (typeof currentValue[field] === 'number') {
      return accumulator + (currentValue[field] as unknown as number);
    }
    return accumulator;
  }, 0);
}

export function toggleElement<T>(set: T[], element: T, status?: boolean): T[] {
  if (status === undefined) {
    if (set.includes(element)) {
      return set.filter((e) => e !== element);
    } else {
      return [...set, element];
    }
  } else {
    if (status) {
      if (set.includes(element)) {
        return set;
      } else {
        return [...set, element];
      }
    } else {
      if (set.includes(element)) {
        return set.filter((e) => e !== element);
      } else {
        return set;
      }
    }
  }
}

export function groupBy<Element, Key extends object>(
  array: Element[],
  grouper: (e: Element) => Key | null
): Array<[Key, Element[]]> {
  const groups: Map<Key, Element[]> = new Map();
  array.forEach((el) => {
    const indexingKey = grouper(el);
    if (!indexingKey) return;
    if (groups.get(indexingKey)) {
      groups.get(indexingKey)?.push(el);
    } else {
      groups.set(indexingKey, [el]);
    }
  });
  return Array.from(groups.entries());
}
